// spec 260509-system-monitor B-1：共享系统监控数据 store。
//
// 三类数据源（design.md §3.1）：
//
//   info        /system/info     启动 1 次 + 每 60s 重拉    info / infoFetchedAt
//   metrics     /system/metrics  1s                       latest / series / metricsPollState
//   services    /services        5s                       services / servicesPollState
//
// 关键不变式：
//
// - 60 点环形缓冲仅作用于 ``metrics.series``；info / services 直接覆盖最新快照。
// - 网络 / 磁盘速率前端差分，差分**用后端 timestamp 而不是浏览器收到响应时间**
//   （patch 5）。同一份缓存命中（response.timestamp 不变）→ 不推 series 新点。
// - ``document.visibilitychange``：hidden ≥ 30s → 三个轮询全部 ``stop``，
//   ``series`` 清空；visible → 三个轮询恢复，曲线从空开始重新累计。
// - **不做**应用级 token / auth 缓存——store 只持有系统采样，无 user 数据。

import { create } from 'zustand';
import { http } from '@blade/os-sdk';

import type {
  ServiceCard,
  SystemInfo,
  SystemMetrics,
} from './system-types';

// ---------------------------------------------------------------------------
// 常量
// ---------------------------------------------------------------------------

export const RING_CAPACITY = 60;
const METRICS_POLL_MS = 1_000;
const SERVICES_POLL_MS = 5_000;
const INFO_REFRESH_MS = 60_000;
const HIDDEN_PAUSE_MS = 30_000;

// ---------------------------------------------------------------------------
// 环形缓冲
// ---------------------------------------------------------------------------

export interface RingBuffer<T> {
  capacity: number;
  buf: Array<T | null>;
  head: number; // 下一个写入位置
  size: number; // 已写入数量（≤ capacity）
}

export function createRingBuffer<T>(capacity: number = RING_CAPACITY): RingBuffer<T> {
  return { capacity, buf: new Array(capacity).fill(null), head: 0, size: 0 };
}

export function pushRing<T>(ring: RingBuffer<T>, value: T | null): RingBuffer<T> {
  // 不做 in-place 修改，便于 zustand subscribers 通过引用比较检测到变化。
  const buf = ring.buf.slice();
  buf[ring.head] = value;
  return {
    capacity: ring.capacity,
    buf,
    head: (ring.head + 1) % ring.capacity,
    size: Math.min(ring.size + 1, ring.capacity),
  };
}

/** 把环形缓冲按时间顺序展开成线性数组（最旧 → 最新），含 null。供 sparkline 渲染。 */
export function ringToSeries<T>(ring: RingBuffer<T>): Array<T | null> {
  const out: Array<T | null> = [];
  if (ring.size < ring.capacity) {
    // 还没写满：写入索引 0..size-1，按时间顺序就是 0..size-1。
    for (let i = 0; i < ring.size; i++) out.push(ring.buf[i] ?? null);
    return out;
  }
  // 写满后：从 head 开始绕一圈。
  for (let i = 0; i < ring.capacity; i++) {
    const idx = (ring.head + i) % ring.capacity;
    out.push(ring.buf[idx] ?? null);
  }
  return out;
}

/**
 * 把 ``RingBuffer<number[]>`` (每点是一个数组, 例如 per_cpu_percent / gpu_util)
 * 按 ``coreIndex`` 抽出该核的 60 点时间序列. 缺失或越界 → null. 用于 CpuPanel
 * 的"显示每核"模式渲染网格 sparkline.
 */
export function ringToCoreSeries(
  ring: RingBuffer<number[]>,
  coreIndex: number,
): Array<number | null> {
  const series = ringToSeries(ring);
  return series.map((arr) => {
    if (!Array.isArray(arr)) return null;
    const v = arr[coreIndex];
    return typeof v === 'number' ? v : null;
  });
}

// ---------------------------------------------------------------------------
// 速率差分
// ---------------------------------------------------------------------------

/**
 * 前端做 counter 差分（design.md §3.1）。
 *
 * - 首点 (``prev === null``) → 返回 null（前端按 ``--`` 渲染）。
 * - counter 回绕 / 网卡重置（curr < prev）→ 返回 null（曲线断点）。
 * - 时间倒退或 dt = 0 → 返回 null（cache hit 时 timestamp 完全相同会走这里）。
 * - 否则返回 ``(curr - prev) / dt`` bytes/sec。
 */
export function computeRate(
  curr: number,
  currTs: number,
  prev: number | null,
  prevTs: number | null,
): number | null {
  if (prev === null || prevTs === null) return null;
  if (curr < prev) return null;
  const dt = (currTs - prevTs) / 1000;
  if (dt <= 0) return null;
  return (curr - prev) / dt;
}

// ---------------------------------------------------------------------------
// Store state
// ---------------------------------------------------------------------------

export type PollState = 'idle' | 'running' | 'paused';

export interface SystemMetricsSeries {
  cpu_percent: RingBuffer<number>;
  /** 每核使用率 60s 序列；单点 = 该秒所有逻辑核 ``per_cpu_percent``。空数组
   *  代表采样缺失。供 CpuPanel 在"显示每核"模式下渲染网格。 */
  per_cpu_percent: RingBuffer<number[]>;
  mem_percent: RingBuffer<number>;
  swap_percent: RingBuffer<number>;
  /** 单点 = 该秒所有 GPU 利用率（按 index 顺序）。空数组代表当时无 GPU 或采样失败。 */
  gpu_util: RingBuffer<number[]>;
  /** 风险最高传感器的当前温度。无传感器 → null。 */
  temp_main: RingBuffer<number>;
  /** 全设备聚合的网络下行 bps（已差分），无值 → null（首点 / cache hit / 回绕）。 */
  net_recv_bps: RingBuffer<number>;
  net_sent_bps: RingBuffer<number>;
  /** 全设备聚合的磁盘读写 bps（已差分）。 */
  disk_read_bps: RingBuffer<number>;
  disk_write_bps: RingBuffer<number>;
}

function emptySeries(): SystemMetricsSeries {
  return {
    cpu_percent: createRingBuffer<number>(),
    per_cpu_percent: createRingBuffer<number[]>(),
    mem_percent: createRingBuffer<number>(),
    swap_percent: createRingBuffer<number>(),
    gpu_util: createRingBuffer<number[]>(),
    temp_main: createRingBuffer<number>(),
    net_recv_bps: createRingBuffer<number>(),
    net_sent_bps: createRingBuffer<number>(),
    disk_read_bps: createRingBuffer<number>(),
    disk_write_bps: createRingBuffer<number>(),
  };
}

interface InternalState {
  info: SystemInfo | null;
  infoFetchedAt: number | null;
  latest: SystemMetrics | null;
  /** 上一份 metrics 的 timestamp（毫秒），用于 cache-hit 检测。 */
  lastSampledMs: number | null;
  series: SystemMetricsSeries;
  metricsPollState: PollState;
  services: ServiceCard[];
  servicesFetchedAt: number | null;
  servicesPollState: PollState;
  infoPollState: PollState;
}

export interface SystemMetricsStore extends InternalState {
  startPolling: () => void;
  stopPolling: () => void;
  /** 测试 helper：清空 series + 重置最近一次采样时间（visibility 切换路径也用）。 */
  resetSeries: () => void;
  /** 测试 helper：拆分注入采样，便于 vitest 控制时间。 */
  __pushMetricsForTest: (m: SystemMetrics) => void;
  __pushInfoForTest: (i: SystemInfo) => void;
  __pushServicesForTest: (s: ServiceCard[]) => void;
}

// ---------------------------------------------------------------------------
// 模块级 timer 句柄（zustand state 之外，避免 set 导致重渲染）
// ---------------------------------------------------------------------------

interface Timers {
  metrics: ReturnType<typeof setInterval> | null;
  services: ReturnType<typeof setInterval> | null;
  info: ReturnType<typeof setInterval> | null;
  hiddenAt: number | null;
  visibilityHandler: (() => void) | null;
}

const timers: Timers = {
  metrics: null,
  services: null,
  info: null,
  hiddenAt: null,
  visibilityHandler: null,
};

// ---------------------------------------------------------------------------
// fetch 入口
// ---------------------------------------------------------------------------

// http() baseURL 已是 /api/v1，所以这里只用相对路径，与 ServiceStatusCards /
// ModelMetricsExpanded 等现有调用对齐（spec 260509 patch B-2）。
async function fetchInfo(): Promise<SystemInfo> {
  const r = await http().get<SystemInfo>('/system/info');
  return r.data;
}

async function fetchMetrics(): Promise<SystemMetrics> {
  const r = await http().get<SystemMetrics>('/system/metrics');
  return r.data;
}

async function fetchServices(): Promise<ServiceCard[]> {
  const r = await http().get<ServiceCard[]>('/services');
  return r.data;
}

// ---------------------------------------------------------------------------
// store 实现
// ---------------------------------------------------------------------------

export const useSystemMetricsStore = create<SystemMetricsStore>((set, get) => {
  function applyMetrics(m: SystemMetrics): void {
    const tsMs = m.timestamp ? Date.parse(m.timestamp) : Date.now();

    // 200ms 路由缓存命中 → 后端 timestamp 相同，前端不推新点。
    const lastMs = get().lastSampledMs;
    if (lastMs !== null && tsMs === lastMs) {
      // 仅刷新 latest（瞬时值字段消费方仍能读最新组装），series 不变。
      set({ latest: m });
      return;
    }

    const prev = get().latest;
    const prevMs = lastMs;
    const series = get().series;

    const next: SystemMetricsSeries = {
      cpu_percent: pushRing(series.cpu_percent, m.cpu_percent ?? null),
      per_cpu_percent: pushRing(
        series.per_cpu_percent,
        m.cpu?.per_core ?? m.per_cpu_percent ?? null,
      ),
      mem_percent: pushRing(series.mem_percent, m.mem_percent ?? null),
      swap_percent: pushRing(
        series.swap_percent,
        m.memory?.swap_percent ?? null,
      ),
      gpu_util: pushRing(
        series.gpu_util,
        m.gpus.map((g) => g.util_percent),
      ),
      temp_main: pushRing(series.temp_main, pickHottestSensor(m)),
      net_recv_bps: pushRing(
        series.net_recv_bps,
        computeRate(
          m.network?.aggregate_bytes_recv ?? m.net.bytes_recv,
          tsMs,
          prev?.network?.aggregate_bytes_recv ?? prev?.net.bytes_recv ?? null,
          prevMs,
        ),
      ),
      net_sent_bps: pushRing(
        series.net_sent_bps,
        computeRate(
          m.network?.aggregate_bytes_sent ?? m.net.bytes_sent,
          tsMs,
          prev?.network?.aggregate_bytes_sent ?? prev?.net.bytes_sent ?? null,
          prevMs,
        ),
      ),
      disk_read_bps: pushRing(
        series.disk_read_bps,
        computeRate(
          totalDiskRead(m),
          tsMs,
          prev ? totalDiskRead(prev) : null,
          prevMs,
        ),
      ),
      disk_write_bps: pushRing(
        series.disk_write_bps,
        computeRate(
          totalDiskWrite(m),
          tsMs,
          prev ? totalDiskWrite(prev) : null,
          prevMs,
        ),
      ),
    };

    set({ latest: m, lastSampledMs: tsMs, series: next });
  }

  async function tickMetrics(): Promise<void> {
    if (get().metricsPollState !== 'running') return;
    try {
      const m = await fetchMetrics();
      // 防御：测试 mock 可能返回 null；生产后端不会。
      if (m !== null && m !== undefined) applyMetrics(m);
    } catch {
      // 单次失败：不动 series（不推断点 → 前端瞬时值显示上次值或 ``--``）。
      // 连续失败的"采集暂时不可用"提示由 panel 自己根据时间判断。
    }
  }

  async function tickServices(): Promise<void> {
    if (get().servicesPollState !== 'running') return;
    try {
      const s = await fetchServices();
      // 防御：后端在异常路径下偶尔返回 null；强制收敛到 array。
      set({ services: Array.isArray(s) ? s : [], servicesFetchedAt: Date.now() });
    } catch {
      // services 拉不到：保留旧 services，前端按 fetchedAt 判断陈旧度。
    }
  }

  async function tickInfo(): Promise<void> {
    if (get().infoPollState !== 'running') return;
    try {
      const i = await fetchInfo();
      if (i !== null && i !== undefined) set({ info: i, infoFetchedAt: Date.now() });
    } catch {
      // info 拉不到：保留旧 info（hostname 这些不会变）。
    }
  }

  function startPolling(): void {
    if (timers.metrics !== null) return; // 已经在跑
    set({
      metricsPollState: 'running',
      servicesPollState: 'running',
      infoPollState: 'running',
    });
    void tickInfo();
    void tickMetrics();
    void tickServices();
    timers.metrics = setInterval(tickMetrics, METRICS_POLL_MS);
    timers.services = setInterval(tickServices, SERVICES_POLL_MS);
    timers.info = setInterval(tickInfo, INFO_REFRESH_MS);
  }

  function stopPolling(): void {
    if (timers.metrics !== null) {
      clearInterval(timers.metrics);
      timers.metrics = null;
    }
    if (timers.services !== null) {
      clearInterval(timers.services);
      timers.services = null;
    }
    if (timers.info !== null) {
      clearInterval(timers.info);
      timers.info = null;
    }
    set({
      metricsPollState: 'paused',
      servicesPollState: 'paused',
      infoPollState: 'paused',
    });
  }

  function resetSeries(): void {
    set({ series: emptySeries(), lastSampledMs: null });
  }

  return {
    info: null,
    infoFetchedAt: null,
    latest: null,
    lastSampledMs: null,
    series: emptySeries(),
    metricsPollState: 'idle',
    services: [],
    servicesFetchedAt: null,
    servicesPollState: 'idle',
    infoPollState: 'idle',
    startPolling,
    stopPolling,
    resetSeries,
    __pushMetricsForTest: applyMetrics,
    __pushInfoForTest: (i: SystemInfo) =>
      set({ info: i, infoFetchedAt: Date.now() }),
    __pushServicesForTest: (s: ServiceCard[]) =>
      set({ services: s, servicesFetchedAt: Date.now() }),
  };
});

// ---------------------------------------------------------------------------
// visibility 处理（hidden ≥ 30s 暂停 + reset；visible 恢复）
// ---------------------------------------------------------------------------

export function attachVisibilityHandler(): () => void {
  if (typeof document === 'undefined') return () => undefined;
  if (timers.visibilityHandler !== null) return () => undefined;

  const onVis = (): void => {
    if (document.visibilityState === 'hidden') {
      timers.hiddenAt = Date.now();
      // 立刻不停：先标记，30s 内回前台直接接着跑（避免短切回时浪费 60s 重新累计）。
      // 30s 后才真正 stop + reset。
      const hiddenAt = timers.hiddenAt;
      setTimeout(() => {
        if (timers.hiddenAt === hiddenAt && document.visibilityState === 'hidden') {
          useSystemMetricsStore.getState().stopPolling();
          useSystemMetricsStore.getState().resetSeries();
        }
      }, HIDDEN_PAUSE_MS);
    } else {
      timers.hiddenAt = null;
      const state = useSystemMetricsStore.getState();
      if (state.metricsPollState !== 'running') {
        state.startPolling();
      }
    }
  };

  document.addEventListener('visibilitychange', onVis);
  timers.visibilityHandler = onVis;
  return () => {
    if (timers.visibilityHandler !== null) {
      document.removeEventListener('visibilitychange', timers.visibilityHandler);
      timers.visibilityHandler = null;
    }
  };
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function pickHottestSensor(m: SystemMetrics): number | null {
  const t = m.temperature;
  if (!t || !t.available || t.sensors.length === 0) return null;
  // patch 11 of design.md §4.2：风险 = current / threshold[kind]，没本期阈值表的话
  // 先按 raw current 取最大；前端 derive 也用同一个 series，渲染时再做风险归一。
  return Math.max(...t.sensors.map((s) => s.current));
}

function totalDiskRead(m: SystemMetrics): number {
  return (m.disk?.io ?? []).reduce((a, x) => a + x.bytes_read, 0);
}

function totalDiskWrite(m: SystemMetrics): number {
  return (m.disk?.io ?? []).reduce((a, x) => a + x.bytes_write, 0);
}

// ---------------------------------------------------------------------------
// Selectors（spec B-1 / patch B-3）
// ---------------------------------------------------------------------------
//
// 与 zustand ``useSystemMetricsStore(selector)`` 配合使用，避免 panel 直接拿
// 整个 state 引发不必要的重渲染；同时把"如何从 services 找一个特定 kind"这种
// 业务逻辑收敛到 store 层，方便后续维护。

export const selectInfo = (s: SystemMetricsStore): SystemInfo | null => s.info;

export const selectLatestMetrics = (s: SystemMetricsStore): SystemMetrics | null =>
  s.latest;

export const selectSeries = <K extends keyof SystemMetricsSeries>(
  key: K,
): ((s: SystemMetricsStore) => SystemMetricsSeries[K]) => (s) => s.series[key];

export const selectServices = (s: SystemMetricsStore): ServiceCard[] => s.services;

/** 按 kind 找模型服务卡（``llm`` / ``asr``）；没有时返回 null。 */
export const selectModelService =
  (kind: 'llm' | 'asr') =>
  (s: SystemMetricsStore): ServiceCard | null =>
    s.services.find((card) => card.tier === 'critical' && card.kind === kind) ?? null;

// ---------------------------------------------------------------------------
// 测试入口（vitest 用）
// ---------------------------------------------------------------------------

/** 单测 reset：清状态 + 解绑定时器。**仅供测试**调用。 */
export function __resetForTest(): void {
  if (timers.metrics !== null) clearInterval(timers.metrics);
  if (timers.services !== null) clearInterval(timers.services);
  if (timers.info !== null) clearInterval(timers.info);
  timers.metrics = null;
  timers.services = null;
  timers.info = null;
  timers.hiddenAt = null;
  useSystemMetricsStore.setState({
    info: null,
    infoFetchedAt: null,
    latest: null,
    lastSampledMs: null,
    series: emptySeries(),
    metricsPollState: 'idle',
    services: [],
    servicesFetchedAt: null,
    servicesPollState: 'idle',
    infoPollState: 'idle',
  });
}
